import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import { globalLoggerToken, gwStorefrontAppInfoToken } from "@/tokens";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import { container, instanceCachingFactory, singleton } from "tsyringe";

import type { ModuleInterface } from "@interfaces/ModuleInterface";

import StorefrontValuesGrowaveApiService from "./api_services/StorefrontValuesGrowaveApiService";
import StorefrontValuesManager from "./StorefrontValuesManager/StorefrontValuesManager";
import GrowaveApiStrategy from "./strategies/GrowaveApiStrategy";
import ParseFromJsonStrategy from "./strategies/ParseFromJsonStrategy/ParseFromJsonStrategy";
import { storefrontValuesAuthApiClientToken } from "./tokens";

interface StorefrontValuesModuleExports {
    storefrontValuesManager: StorefrontValuesManager;
}
@singleton()
export class StorefrontValuesModule
    implements ModuleInterface<StorefrontValuesModuleExports>
{
    exports: StorefrontValuesModuleExports;

    constructor(
        private readonly analyticsEventsCollectorModule: AnalyticsEventsCollectorModule,
        private readonly commonModule: CommonModule
    ) {
        const logger = container
            .resolve(globalLoggerToken)
            .newLogger("StorefrontValuesModule");
        const { designModeQueryParamsProvider, locationController } =
            this.commonModule.exports;
        const { eventsCollector, eventsFactory } =
            this.analyticsEventsCollectorModule.exports;

        container.register(storefrontValuesAuthApiClientToken, {
            useFactory: instanceCachingFactory(() => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/common-storefront/v2"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]:
                                    container.resolve(gwStorefrontAppInfoToken)
                                        .currentApp,
                                ...designModeQueryParamsProvider.queryParams,
                            },
                            logger
                        ),
                        eventsCollector,
                        locationController,
                        eventsFactory
                    ),
                    container.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        const storefrontValuesManager = new StorefrontValuesManager(logger);

        storefrontValuesManager.registerStrategies([
            new ParseFromJsonStrategy(logger),
            new GrowaveApiStrategy(
                new StorefrontValuesGrowaveApiService(
                    logger,
                    container.resolve(storefrontValuesAuthApiClientToken)
                )
            ),
        ]);

        this.exports = {
            storefrontValuesManager,
        };
    }

    registerProviders() {
        return {};
    }
}
